import React from 'react';
import {
  Box,
  FormControlLabel,
  Switch,
  Typography,
  Alert,
  Snackbar,
} from '@mui/material';
import { AppConfig, ConfigItem } from '../../../services/configApi';
import { SectionSaveButtons } from './SectionSaveButtons';
import { useSectionConfig } from '../hooks/useSectionConfig';

interface PublishingConfigProps {
  config: AppConfig;
  items: ConfigItem[];
  disabled?: boolean;
}

const PUBLISHING_KEYS: (keyof AppConfig)[] = [
  'auto_lifecycle_progression',
  'auto_release_enabled',
];

export const PublishingConfig: React.FC<PublishingConfigProps> = ({
  config,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  items, // Not used currently but kept for consistency with other config components
  disabled = false,
}) => {
  const {
    currentValues,
    hasChanges,
    saving,
    updateValue,
    saveChanges,
    discardChanges,
  } = useSectionConfig(PUBLISHING_KEYS, config);

  const [notification, setNotification] = React.useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const handleSave = async () => {
    const result = await saveChanges();
    setNotification({
      message: result.message,
      type: result.success ? 'success' : 'error',
    });
  };

  const handleDiscard = () => {
    discardChanges();
    setNotification({
      message: 'Changes discarded',
      type: 'success',
    });
  };

  const closeNotification = () => {
    setNotification(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(currentValues.auto_lifecycle_progression)}
              onChange={e =>
                updateValue('auto_lifecycle_progression', e.target.checked)
              }
              disabled={disabled || saving}
            />
          }
          label="Auto lifecycle progression"
        />
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          When enabled, stories automatically progress through lifecycle phases
          when their database records change
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(currentValues.auto_release_enabled)}
              onChange={e =>
                updateValue('auto_release_enabled', e.target.checked)
              }
              disabled={disabled || saving}
            />
          }
          label="Auto-release stories"
        />
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          When enabled, stories that pass all pre-publish checks will be
          automatically scheduled for publication
        </Typography>
      </Box>

      <SectionSaveButtons
        hasChanges={hasChanges}
        saving={saving}
        onSave={handleSave}
        onDiscard={handleDiscard}
        disabled={disabled}
      />

      <Snackbar
        open={!!notification}
        autoHideDuration={4000}
        onClose={closeNotification}
      >
        <Alert
          onClose={closeNotification}
          severity={notification?.type}
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
